import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useApi } from '../hooks/useApi';

interface SearchResult {
  document_id: string;
  title: string;
  snippet: string;
  score: number;
  state: string;
  freshness_score: number;
  content_type: string;
  tier: number;
}

interface Suggestion {
  term: string;
  path: string;
}

export function SearchPage() {
  const { apiFetch } = useApi();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  // Autocomplete
  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetch<{ suggestions: Suggestion[] }>('/search/suggestions', { params: { q: query } });
        setSuggestions(data.suggestions);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setIsSearching(true);
    setSuggestions([]);
    try {
      const data = await apiFetch<{ results: SearchResult[] }>('/search', {
        method: 'POST',
        body: { query, include_archived: includeArchived, limit: 20 },
      });
      setResults(data.results);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsSearching(false);
    }
  }, [query, includeArchived]);

  const handleFeedback = async (docId: string, helpful: boolean) => {
    try {
      await apiFetch('/search/feedback', { method: 'POST', body: { document_id: docId, helpful } });
    } catch { /* non-critical */ }
  };

  const freshnessIcon = (state: string, score: number) => {
    if (state === 'stale') return '🕐';
    if (score >= 80) return '✓';
    return '⚠️';
  };

  const tierBadge = (tier: number) => {
    const colors: Record<number, 'red' | 'blue' | 'grey' | 'green'> = { 1: 'red', 2: 'blue', 3: 'grey', 4: 'green' };
    const labels: Record<number, string> = { 1: 'Critical', 2: 'Team', 3: 'Project', 4: 'Frozen' };
    const label = labels[tier];
    if (!label) return null;
    return <Badge color={colors[tier]}>{label}</Badge>;
  };

  return (
    <SpaceBetween size="l">
      <Box textAlign="center" padding={{ top: 'xl', bottom: 's' }}>
        <Box variant="h1">Knowledge Base</Box>
        <Box variant="p" color="text-body-secondary">Search across all enterprise knowledge</Box>
      </Box>

      {/* Search bar */}
      <div style={{ maxWidth: '42rem', marginInline: 'auto' }}>
        <SpaceBetween size="s">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <Autosuggest
                value={query}
                onChange={({ detail }) => setQuery(detail.value)}
                onSelect={({ detail }) => { setQuery(detail.value); setSuggestions([]); }}
                onKeyDown={({ detail }) => { if (detail.key === 'Enter') { handleSearch(); } }}
                options={suggestions.map((s) => ({ value: s.term, description: s.path }))}
                filteringType="manual"
                enteredTextLabel={(value) => `Search for "${value}"`}
                placeholder="Search documents..."
                ariaLabel="Search documents"
                empty="No suggestions"
              />
            </div>
            <Button variant="primary" onClick={handleSearch} disabled={isSearching} loading={isSearching}>
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {/* Filters */}
          <Checkbox
            checked={includeArchived}
            onChange={({ detail }) => setIncludeArchived(detail.checked)}
          >
            Include archived
          </Checkbox>
        </SpaceBetween>
      </div>

      {/* Results */}
      <div style={{ maxWidth: '48rem', marginInline: 'auto' }}>
        <SpaceBetween size="m">
          {results.map((r) => (
            <Container key={r.document_id}>
              <SpaceBetween size="xs">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <Box variant="h3" padding="n">
                    <Link to={`/documents/${r.document_id}`}>
                      <SpaceBetween size="xxs" direction="horizontal">
                        <span aria-hidden="true">{freshnessIcon(r.state, r.freshness_score)}</span>
                        <span>{r.title}</span>
                      </SpaceBetween>
                    </Link>
                  </Box>
                  <SpaceBetween size="xs" direction="horizontal">
                    {tierBadge(r.tier)}
                    <Box variant="small" color="text-body-secondary">{r.content_type}</Box>
                  </SpaceBetween>
                </div>

                <Box variant="p" color="text-body-secondary">{r.snippet}</Box>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <SpaceBetween size="s" direction="horizontal">
                    <Box variant="small" color="text-body-secondary">Score: {r.score.toFixed(3)}</Box>
                    <Box variant="small" color="text-body-secondary">Freshness: {r.freshness_score}/100</Box>
                    {r.state === 'stale' && (
                      <Box variant="small" color="text-status-warning" fontWeight="bold">⚠ Stale</Box>
                    )}
                  </SpaceBetween>
                  <SpaceBetween size="xxs" direction="horizontal">
                    <Button
                      variant="inline-icon"
                      iconName="thumbs-up"
                      ariaLabel="Helpful"
                      onClick={() => handleFeedback(r.document_id, true)}
                    />
                    <Button
                      variant="inline-icon"
                      iconName="thumbs-down"
                      ariaLabel="Not helpful"
                      onClick={() => handleFeedback(r.document_id, false)}
                    />
                  </SpaceBetween>
                </div>
              </SpaceBetween>
            </Container>
          ))}

          {results.length === 0 && query && !isSearching && (
            <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'xl' }}>
              No results found for "{query}"
            </Box>
          )}
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
